import React, { useState, useEffect } from "react";
import "./VentasAdmin.css";
import Permiso from "../../router/Permiso";
import { useNavigate } from "react-router-dom";
import { Pencil, View } from "lucide-react";
import { SaleService } from "../../services/SaleService";

const ListadoVentas = ({ token, refreshTrigger = 0 }) => {
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fechaInicio, setFechaInicio] = useState("");
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [fechaFin, setFechaFin] = useState("");
  const [buscarDNI, setBuscarDNI] = useState("");
  const [buscarNombre, setBuscarNombre] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchVentas();
  }, [currentPage, fechaInicio, fechaFin, refreshTrigger]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (buscarDNI || buscarNombre) {
        setCurrentPage(1);
        fetchVentas();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [buscarDNI, buscarNombre]);

  const fetchVentas = async () => {
    setLoading(true);
    setError("");
    try {
      const { ventas, totalPaginas, totalRegistros } = await SaleService.fetchVentas({
        token,
        pagina: currentPage,
        pageSize: 20,
        desde: fechaInicio,
        hasta: fechaFin,
        dni: buscarDNI,
        nombre: buscarNombre,
      });

      setVentas(ventas);
      setTotalPages(totalPaginas);
      setTotalRegistros(totalRegistros);
    } catch (err) {
      setError("Error al cargar las ventas");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConsultarVenta = async (venta) => {
    navigate(`/admin/ventas/consultar/${venta.id}`);
  };

  const handleUpdateVenta = async (venta) => {
    navigate(`/admin/ventas/${venta.id}`);
  };

  const exportarExcel = async () => {
    try {
      const filtros = {
        desde: fechaInicio,
        hasta: fechaFin,
        dni: buscarDNI,
        nombre: buscarNombre,
      };

      const blob = await SaleService.exportarVentasExcel(token, filtros);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ventas.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    }
  };

  return (
    <div className="ventas-admin-container">
      <div className="ventas-table-header">
        <h2 className="ventas-table-title">Listado de Ventas</h2>
        <div className="productos-actions">
          <Permiso permiso="export_venta">
            <button onClick={exportarExcel} className="btn-exportar">
              Exportar a Excel
            </button>
          </Permiso>
        </div>
      </div>

      {/* Filtros */}
      <div className="usuarios-filtros">
        <div className="filtro-item">
          <label htmlFor="fecha-inicio">Fecha Inicio:</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>
        <div className="filtro-item">
          <label htmlFor="fecha-fin">Fecha Fin:</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>
        <div className="filtro-item">
          <label htmlFor="numero-documento">Número del Documento:</label>
          <input
            type="text"
            value={buscarDNI}
            onChange={(e) => setBuscarDNI(e.target.value)}
            placeholder="Ingrese número  del cliente"
          />
        </div>
        <div className="filtro-item">
          <label htmlFor="buscar-cliente">Buscar por Cliente:</label>
          <input
            type="text"
            value={buscarNombre}
            onChange={(e) => setBuscarNombre(e.target.value)}
            placeholder="Nombre, apellido o nombre completo"
          />
        </div>
        <div className="filtro-item">
          <button onClick={fetchVentas} className="btn-filtrar">
            Buscar
          </button>
        </div>
        <div className="filtro-item">
          <button
            onClick={() => {
              setFechaInicio("");
              setFechaFin("");
              setBuscarDNI("");
              setBuscarNombre("");
              setCurrentPage(1);
              fetchVentas();
            }}
            className="btn-limpiar"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Cargando ventas...</div>
      ) : (
        <>
          <div className="ventas-table-wrapper">
            <table className="ventas-table">
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Vendedor</th>
                  <th>Supervisor</th>
                  <th>Cliente</th>
                  <th>Tipo de Documento</th>
                  <th>Numero</th>
                  <th>Estado</th>
                  <th>Estado Servicio</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map((venta) => {
                  return (
                    <tr key={venta.id}>
                      <td>{venta.codigo_venta_cliente}</td>
                      <td>
                        {venta.nombre_vendedor} {venta.apellido_vendedor}
                      </td>
                      <td>{venta.supervisor}</td>
                      <td>
                        {venta.tipo_documento === 'RUC'
                          ? venta.cliente_razon_social
                          : `${venta.cliente_nombre} ${venta.cliente_apellido_paterno} ${venta.cliente_apellido_materno}`}
                      </td>
                      <td>{venta.tipo_documento}</td>
                      <td>{venta.numero_documento}</td>
                      <td>{venta.estado_cliente}</td>
                      <td>{venta.estado_servicio}</td>
                      <td>
                        {new Date(venta.fecha_venta).toLocaleDateString()}
                      </td>
                      <td className="ventas-acciones">
                        <Permiso permiso="consultar_venta">
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => handleConsultarVenta(venta)}

                          >
                            <View size={20} />
                          </span>
                        </Permiso>
                        <Permiso permiso="update_venta">
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => handleUpdateVenta(venta)}
                          >
                            <Pencil size={20} />
                          </span>
                        </Permiso>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
            {" "}
            Total de registros: {totalRegistros}{" "}
          </span>
          {/* Paginación */}
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ListadoVentas;
